<template>
  <DialogSlot
    :id="slotId"
    ref="set-vendor-modal"
    :modalBtn="$t('action.setEmail4Contractor')"
    :modalBtnClass="'btn--darkblue'"
    :modalHeader="$t('settings.user.vendor.setContractorEmail')"
    :showCloseBtn="false"
    :beforeOpenModal="beforeOpenModal"
    :afterCloseModal="resetData"
  >
    <div slot="content">
      <label
        class="required-flag"
        for="settings-vendors-add-number"
      >
        {{ $t('settings.user.vendor.username') }} ({{ $t('settings.user.vendor.email') }})
      </label>
      <input
        v-model="username"
        v-fusioninputcheck="{
          type: 'string',
          patternOnly: validateUsername,
          classes: 'tooltip-class',
          tip: usernameTip,
          required: true
        }"
        :class="emailErrClass"
        type="text"
        pattern="email"
        :placeholder="$i18n.t('settings.user.vendor.email')"
      >
      <label
        class="required-flag"
        for="settings-vendors-add-number"
      >
        {{ $t('settings.user.vendor.number') }}
      </label>
      <HKLSelect
        :value="companyToSetId"
        class="align-center"
        :class="extIdErrClass"
        :options="companyNumberHklOptions"
        :showSearch="true"
        placeholder=""
        @change="onCompanyChange"
      />

      <label class="required-flag">{{ $t('company.basic.companyName') }}</label>
      <HKLSelect
        :value="companyToSetId"
        class="align-center"
        :class="companyErrClass"
        :options="companyNameHklOptions"
        :showSearch="true"
        placeholder=""
        @change="onCompanyChange"
      />

      <label class="required-flag">{{ $t('company.basic.departmentName') }}</label>
      <div>
        <HKLMultiSelect
          v-model="selectedDepartmentIds"
          class="align-center multi-value"
          :class="departmentErrClass"
          :options="departmentHklOptions"
          :showSearch="false"
          placeholder=""
        />
      </div>

      <label
        class="required-flag"
        for="settings-vendors-add-contactAdmin"
      >
        {{ $t('settings.user.vendor.contactAdminName') }}
      </label>
      <input
        id="settings-vendors-add-contactAdmin"
        v-model="contactAdmin"
        v-fusioninputcheck="{
          type: 'string',
          pattern: regex.string,
          classes: 'tooltip-class'
        }"
        type="text"
        :placeholder="$i18n.t('settings.user.vendor.contactAdminName')"
        required
      >

      <label
        class="required-flag"
        for="settings-vendors-add-contactPhone"
      >
        {{ $t('settings.user.vendor.contactPhone') }}
      </label>
      <input
        id="settings-vendors-add-contactPhone"
        v-model="contactPhone"
        v-fusioninputcheck="{
          type: 'string',
          pattern: regex.phone,
          classes: 'tooltip-class',
          tip: tips.phone
        }"
        type="number"
        :placeholder="$i18n.t('settings.user.vendor.contactPhone')"
      >
    </div>
    <div slot="buttons">
      <div class="row align-right nav_btn">
        <button
          id="tender-general-contractData-cancel"
          type="button"
          class="btn btn--darkblue"
          @click="closeModal"
        >
          {{ $t('action.cancel') }}
        </button>
        <HKLButton
          action="confirm"
          color="orange"
          @click="() => addVendor()"
        />
      </div>
    </div>
  </DialogSlot>
</template>

<script>
import lodash from 'lodash'
import { computed, ref, unref } from 'vue-demi'
import { checkIfProjectRep, createAccount } from '@/api/accounts/request'
import DialogSlot from '@/controls/DialogSlot'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { UserDomain } from '@/utils/consts'
import EventBus, { EVENT_FLAG } from '@/utils/eventBus'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import * as regex from '@/utils/regex'
import HKLButton from '../../../controls/Button/HKLButton.vue'
import { addSubAccountPerm } from './consts'
import { useValidateInternalUsername } from './hook'

export default {
  name: 'AddVendor',
  components: {
    HKLButton,
    DialogSlot,
    HKLSelect,
    HKLMultiSelect,
  },
  props: {
    companyList: {
      type: Array,
      default: () => [],
    },
    departmentList: {
      type: Array,
      default: () => [],
    },
    parent: {
      type: String,
      default: '',
    },
  },
  setup () {
    const username = ref('')
    const emailInvalid = ref(false)
    const departmentInvalid = ref(false)
    const externalIdInvalid = ref(false)
    const fullNameInvalid = ref(false)
    const companyToSet = ref('')
    const validateEmailIfProjectRep = async () => {
      const result = await checkIfProjectRep(
        unref(companyToSet).id, unref(username), true,
      )
      emailInvalid.value = result
      return result
    }
    const { validateUsername, usernameTip } = useValidateInternalUsername()
    return {
      usernameTip,
      username,
      companyToSet,
      extIdErrClass: computed(() => {
        return { 'error-highlight': unref(externalIdInvalid) }
      }),
      companyErrClass: computed(() => {
        return { 'error-highlight': unref(fullNameInvalid) }
      }),
      departmentErrClass: computed(() => {
        return { 'error-highlight': unref(departmentInvalid) }
      }),
      emailErrClass: computed(() => {
        return { 'is-invalid-input': unref(emailInvalid) }
      }),
      emailInvalid,
      departmentInvalid,
      externalIdInvalid,
      fullNameInvalid,
      validateUsername,
      validateEmailIfProjectRep,
    }
  },
  data () {
    const tips = {
      email: this.$i18n.t('hint.inputTip.email'),
      phone: this.$i18n.t('hint.inputTip.phone'),
      string: this.$i18n.t('hint.inputTip.string'),
    }
    return {
      slotId: 'settings-users-contractor-set-email',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: '',
      phoneNumber: '',
      contactAdmin: '',
      contactPhone: '',
      regex,
      tips,
      selectedDepartments: [],
    }
  },
  computed: {
    companyNumberList () {
      return lodash.uniqBy(this.companyList, 'id')
    },
    companyNameList () {
      return lodash.uniqBy(this.companyList, 'name')
    },
    companyName () {
      return this.companyToSet ? this.companyToSet.name : '--'
    },
    selectedDepartmentNames () {
      return lodash.uniqBy(this.selectedDepartments, 'id')
    },
    selectedDepartmentIds: {
      get () {
        return this.selectedDepartmentNames.map(({ id }) => id)
      },
      set (ids = []) {
        this.selectedDepartments = ids.map((id) => this.departmentList.find(department => department.id === id)).filter(Boolean)
      },
    },
    companyToSetId () {
      return lodash.get(this.companyToSet, 'id')
    },
    companyNumberHklOptions () {
      return this.companyNumberList.map((company) => ({
        name: company.id,
        value: company.id,
      }))
    },
    companyNameHklOptions () {
      return this.companyNameList.map((company) => ({
        name: company.name,
        value: company.id,
      }))
    },
    departmentHklOptions () {
      return this.departmentList.map((department) => ({
        name: department.shortName || department.name,
        value: department.id,
      }))
    },
  },
  methods: {
    onCompanyChange (companyId) {
      this.companyToSet = this.companyList.find(company => company.id === companyId) || ''
      this.selectedDepartments = []
    },
    resetInvalidFields () {
      this.externalIdInvalid = false
      this.fullNameInvalid = false
      this.departmentInvalid = false
    },
    assembleRequestData () {
      const departmentIds = this.selectedDepartmentNames.map(({ id }) => id)

      const result = {}
      result.contact = {
        NAME: [{description: 'Name', value: this.contactAdmin.trim()}],
        PHONE_NUMBER: [{description: 'Contact Phone Number', value: this.contactPhone.trim()}],
        EMAIL: [{description: 'Email', value: this.username.trim()}],
      }
      result.user = {
        externalId: this.vendorId,
        isAdmin: true,
        domain: UserDomain.CMS,
        username: this.username.trim(),
        departmentIds,
        perms: {
          [addSubAccountPerm.resource]: {
            [addSubAccountPerm.action]: addSubAccountPerm.attributes,
          },
        },
      }
      return result
    },
    resetData () {
      fusionInputCheckUtil.resetAllFields()
      this.companyToSet = ''
      this.selectedDepartments = []
      this.vendorId = ''
      this.fullName = ''
      this.addressLine1 = ''
      this.addressLine2 = ''
      this.addressLine3 = ''
      this.addressLine4 = ''
      this.phoneNumber = ''
      this.contactAdmin = ''
      this.contactPhone = ''
      this.username = ''
    },
    trimData () {
      this.vendorId = deep(this.companyToSet, 'id')
      this.fullName = deep(this.companyToSet, 'name')
      this.addressLine1 = this.addressLine1.trim()
      this.addressLine2 = this.addressLine2.trim()
      this.addressLine3 = this.addressLine3.trim()
      this.addressLine4 = this.addressLine4.trim()
      this.phoneNumber = this.phoneNumber.trim()
      this.contactAdmin = this.contactAdmin.trim()
      this.contactPhone = this.contactPhone.trim()
      this.username = this.username.trim()
    },
    beforeOpenModal () {
      this.resetInvalidFields()
      return true
    },
    closeModal () {
      this.resetData()
      this.$refs['set-vendor-modal'].closeModal()
    },

    validate () {
      fusionInputCheckUtil.validateAllFields()
      this.externalIdInvalid = !this.vendorId
      this.fullNameInvalid = !this.fullName
      const hasDepartment = this.selectedDepartments.length > 0
      this.departmentInvalid = !hasDepartment
      return this.vendorId && $('input.is-invalid-input').length === 0 && $('input.fusion-input-check-is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid(`#${this.slotId}`) && hasDepartment
    },
    async addVendor () {
      this.trimData()
      const hasUserOnChoiceDept = this.validateMasterUserInDepartment()
      const isUsedOnProjectRep = await this.validateEmailIfProjectRep()
      const isInvalidEmail = !hasUserOnChoiceDept || isUsedOnProjectRep
      if (isInvalidEmail) {
        return
      }
      if (this.validate()) {
        try {
          const resp = await createAccount(this.assembleRequestData(), false)
          if (resp.data.data) {
            this.closeModal()
            navigation.toPage('vendor-detail', { id: resp.data.data.id, navFrom: this.parent })
          }
          notification.handleResponseAlert(resp, 'setContractorEmail')
          EventBus.$emit(EVENT_FLAG.MODULES_SETTINGS_VENDORS_ADD_VENDOR_SUCCESS)
        } catch (err) {
          notification.handleResponseError(err, 'setContractorEmail')
        }
      } else {
        notification.fireNotification(false, 'Please check your input values')
      }
    },
    validateMasterUserInDepartment () {
      const selectedDepartmentIds = this.selectedDepartments.map(({ id }) => id)
      const users = lodash.get(this.companyToSet, 'users', [])
      const masterUsers = users.filter(user => user.primary)
      const invalidDepartments = masterUsers.reduce((acc, user) => {
        let newAcc = acc
        const departmentIds = user.departments.map(d => d.id)
        const result = lodash.intersection(departmentIds, selectedDepartmentIds)
        if (result.length > 0) {
          newAcc.departmentIds = newAcc.departmentIds.concat(result)
          const displayName = lodash.get(user, 'displayName')
          const username = lodash.get(user, 'username', displayName)
          newAcc.usernames.push(username)
        }
        return newAcc
      }, { departmentIds: [], usernames: [] })
      const isInvalid = invalidDepartments.departmentIds.length > 0

      if (isInvalid) {
        const departments = invalidDepartments.departmentIds.map(id => {
          return this.departmentList.find(d => d.id === id)
        })
        const departmentNames = departments.map(item => (`${item.name}(${item.shortName})`)).join(', ')
        const usernames = invalidDepartments.usernames.join(', ')
        const errMsg = this.$t('hint.duplicateVendorMaster', {
          departmentNames,
          email: usernames,
        })
        notification.error(errMsg)
      }
      return !isInvalid
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/popup';
@import '~styles/common';

input[type='text'], input[type='number'] {
  width: 100%;
}
</style>

<style lang="scss">
#settings-users-contractor-set-email .error-highlight .ant-select-selection,
#settings-users-contractor-set-email .error-highlight .multiselect__input {
  background-color: #f9dcda!important;
}

</style>
